export type ElementWindowLayout = {
    x: number;
    y: number;
    width: number;
    height: number;
};

export type ElementScrollContentLayout = ElementWindowLayout;

type ScrollPosition = {
    x: number;
    y: number;
};

type ElementRect = {
    x?: number;
    y?: number;
    left?: number;
    top?: number;
    width: number;
    height: number;
};

type MeasurableElement = {
    measure?: (
        callback: (
            x: number,
            y: number,
            width: number,
            height: number,
            pageX: number,
            pageY: number,
        ) => void,
    ) => void;
    measureInWindow?: (
        callback: (x: number, y: number, width: number, height: number) => void,
    ) => void;
    getBoundingClientRect?: () => ElementRect;
};

// This provides a consistent interface for measuring an element's position and size
// in the window, regardless of the platform or the element's type.
export function measureElementInWindow(
    element: unknown,
    callback: (layout: ElementWindowLayout) => void,
): boolean {
    const measurableElement = element as MeasurableElement | null | undefined;

    if (!measurableElement) {
        return false;
    }

    // .measure is the classic React Native API available on native component refs. Its callback provides
    // both parent-relative (x, y) and window-relative (pageX, pageY) coordinates; we use the latter.
    // This is not available with React Strict Dom.
    if (typeof measurableElement.measure === 'function') {
        measurableElement.measure((_, __, width, height, pageX, pageY) => {
            callback({ x: pageX ?? _, y: pageY ?? __, width, height });
        });
        return true;
    }

    // .measureInWindow is available with React Strict Dom on native (iOS/Android), where html.* element
    // refs expose the React Native NativeMethods interface. It is also available on classic RN refs.
    if (typeof measurableElement.measureInWindow === 'function') {
        measurableElement.measureInWindow((x, y, width, height) => {
            callback({ x, y, width, height });
        });
        return true;
    }

    // .getBoundingClientRect is the standard web DOM API. With React Strict Dom on web, html.* elements
    // are real DOM nodes, so this is the only measurement API available there.
    if (typeof measurableElement.getBoundingClientRect === 'function') {
        const rect = measurableElement.getBoundingClientRect();
        callback({
            x: rect.x ?? rect.left ?? 0,
            y: rect.y ?? rect.top ?? 0,
            width: rect.width,
            height: rect.height,
        });
        return true;
    }

    return false;
}

// This measures an element using the coordinate space expected by scroll-content visibility
// calculations. Classic React Native measure() exposes parent/content-relative x/y values;
// React Strict DOM refs do not, so window-relative measurement APIs are offset by the current
// scroll position to produce content-space coordinates.
export function measureElementInScrollContent(
    element: unknown,
    scrollPosition: ScrollPosition,
    callback: (layout: ElementScrollContentLayout) => void,
): boolean {
    const measurableElement = element as MeasurableElement | null | undefined;

    if (!measurableElement) {
        return false;
    }

    if (typeof measurableElement.measure === 'function') {
        measurableElement.measure((x, y, width, height) => {
            callback({ x, y, width, height });
        });
        return true;
    }

    if (typeof measurableElement.measureInWindow === 'function') {
        measurableElement.measureInWindow((x, y, width, height) => {
            callback({
                x: x + scrollPosition.x,
                y: y + scrollPosition.y,
                width,
                height,
            });
        });
        return true;
    }

    if (typeof measurableElement.getBoundingClientRect === 'function') {
        const rect = measurableElement.getBoundingClientRect();
        callback({
            x: (rect.x ?? rect.left ?? 0) + scrollPosition.x,
            y: (rect.y ?? rect.top ?? 0) + scrollPosition.y,
            width: rect.width,
            height: rect.height,
        });
        return true;
    }

    return false;
}
